import React, { useState, useEffect } from "react";
import Layout from "../../layouts/default";
import { Form, Button } from "react-bootstrap";
import styles from "./LeadsDataTransferSetting.module.css";
import { updateLeadTransferStatus } from "../../services/preLeads/leadTransfer";
import { fetchLeadTransferStatus } from "../../services/preLeads/leadTransfer";
import { toast } from "react-toastify";
import { fetchBitrixDataTransferAll } from "../../services/preLeads/leadTransfer";
import { bulkUpdateBitrixDataTransfer } from "../../services/preLeads/leadTransfer";

const LeadsDataTransferSetting = () => {
  const [isTransferActive, setIsTransferActive] = useState(false);
  const [dailyUploadCount, setDailyUploadCount] = useState("");
  const [gstPercentage, setGstPercentage] = useState("");
  const [dinPercentage, setDinPercentage] = useState("");
  const [bcmPercentage, setBcmPercentage] = useState("");
  const [gstValue, setGstValue] = useState("");
  const [dinValue, setDinValue] = useState("");
  const [bcmValue, setBcmValue] = useState("");
  const [gstId, setGstId] = useState(null);
  const [dinId, setDinId] = useState(null);
  const [bcmId, setBcmId] = useState(null);
  const [gstRemaining, setGstRemaining] = useState(null);
  const [dinRemaining, setDinRemaining] = useState(null);
  const [bcmRemaining, setBcmRemaining] = useState(null);

  const isFormIncomplete =
    !dailyUploadCount || !gstPercentage || !dinPercentage || !bcmPercentage;

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetchLeadTransferStatus(1);
        setIsTransferActive(response?.data?.status === "YES");
      } catch (e) {
        console.error("Fetch status error:", e);
      }
    };
    fetchStatus();

    const fetchBitrixData = async () => {
      try {
        const res = await fetchBitrixDataTransferAll();
        if (res && res.data && Array.isArray(res.data)) {
          const gstTransfer = res.data.find(
            (item) => item.type === "GST_TRANSFER",
          );
          const dinTransfer = res.data.find(
            (item) => item.type === "DIN_TRANSFER",
          );
          const bcmTransfer = res.data.find(
            (item) => item.type === "BCM_TRANSFER",
          );

          setGstId(gstTransfer?.id || null);
          setDinId(dinTransfer?.id || null);
          setBcmId(bcmTransfer?.id || null);

          setGstRemaining(gstTransfer?.remaining_count ?? null);
          setDinRemaining(dinTransfer?.remaining_count ?? null);
          setBcmRemaining(bcmTransfer?.remaining_count ?? null);

          setGstPercentage(gstTransfer?.percent?.toString() || "");
          setDinPercentage(dinTransfer?.percent?.toString() || "");
          setBcmPercentage(bcmTransfer?.percent?.toString() || "");

          setGstValue(gstTransfer?.value?.toString() || "");
          setDinValue(dinTransfer?.value?.toString() || "");
          setBcmValue(bcmTransfer?.value?.toString() || "");

          const totalValue =
            (gstTransfer?.value || 0) +
            (dinTransfer?.value || 0) +
            (bcmTransfer?.value || 0);
          setDailyUploadCount(totalValue.toString());
        }
      } catch (e) {
        console.error("Fetch Bitrix data error:", e);
      }
    };
    fetchBitrixData();
  }, []);

  useEffect(() => {
    const totalCount = parseFloat(dailyUploadCount) || 0;
    setGstValue(
      dailyUploadCount && gstPercentage
        ? Math.round(
            (totalCount * parseFloat(gstPercentage || 0)) / 100,
          ).toString()
        : "",
    );
    setDinValue(
      dailyUploadCount && dinPercentage
        ? Math.round(
            (totalCount * parseFloat(dinPercentage || 0)) / 100,
          ).toString()
        : "",
    );
    setBcmValue(
      dailyUploadCount && bcmPercentage
        ? Math.round(
            (totalCount * parseFloat(bcmPercentage || 0)) / 100,
          ).toString()
        : "",
    );
  }, [dailyUploadCount, gstPercentage, dinPercentage, bcmPercentage]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that percentages add up to 100
    const total =
      parseFloat(gstPercentage || 0) +
      parseFloat(dinPercentage || 0) +
      parseFloat(bcmPercentage || 0);

    if (total !== 100 && (gstPercentage || dinPercentage || bcmPercentage)) {
      toast.error("The total percentage must equal 100%", { autoClose: 3000 });
      return;
    }

    try {
      // Calculate values based on percentages
      const totalCount = parseFloat(dailyUploadCount);
      const gstVal = Math.round((totalCount * parseFloat(gstPercentage)) / 100);
      const dinVal = Math.round((totalCount * parseFloat(dinPercentage)) / 100);
      const bcmVal = Math.round((totalCount * parseFloat(bcmPercentage)) / 100);

      await bulkUpdateBitrixDataTransfer([
        {
          id: gstId,
          updateFields: {
            value: gstVal,
            percent: parseFloat(gstPercentage),
            status: "ACTIVE",
          },
        },
        {
          id: dinId,
          updateFields: {
            value: dinVal,
            percent: parseFloat(dinPercentage),
            status: "ACTIVE",
          },
        },
        {
          id: bcmId,
          updateFields: {
            value: bcmVal,
            percent: parseFloat(bcmPercentage),
            status: "ACTIVE",
          },
        },
      ]);

      toast.success("Lead transfer settings updated successfully", {
        autoClose: 3000,
      });
    } catch (error) {
      toast.error("Failed to update lead transfer settings", {
        autoClose: 3000,
      });
      console.error("Update error:", error);
    }
  };

  const handleToggle = async (checked) => {
    setIsTransferActive(checked);
    const status = checked ? "YES" : "NO";
    try {
      const response = await updateLeadTransferStatus({ id: 1, status });
      if (status === "YES") {
        toast.success("Campaign Leads Data transfer started", {
          autoClose: 3000,
        });
      } else {
        toast.success("Campaign Leads Data transfer paused", {
          autoClose: 3000,
        });
      }
      console.log("Toggle API response:", response);
    } catch (e) {
      toast.error("Failed to update lead transfer status", { autoClose: 3000 });
      console.error("Toggle API error:", e);
    }
  };

  const handleNumberInput = (setter) => (e) => {
    const value = e.target.value.replace(/[^\d]/g, "");
    setter(value);
  };

  const blockNonNumeric = (e) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const sanitizePaste = (setter) => (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    if (/^\d+$/.test(paste)) {
      setter(paste);
    } else {
      e.preventDefault();
    }
  };

  return (
    <Layout>
      <div className="mb-4">
        <h5 className="fw-semibold" style={{ color: "#6c757d" }}>
          Campaign Leads Data transfer Setting
        </h5>
      </div>

      <div className={styles.formContainer}>
        <Form onSubmit={handleSubmit}>
          {/* Toggle Switch Section */}
          <div className="mb-4">
            <Form.Label className={styles.sectionLabel}>
              Start/ Stop Campaign Leads Data transfer
            </Form.Label>
            <div className={styles.toggleSwitch}>
              <Form.Check
                type="switch"
                id="data-transfer-toggle"
                checked={isTransferActive}
                onChange={(e) => handleToggle(e.target.checked)}
              />
            </div>
          </div>

          {/* Daily Upload Count */}
          <div className="mb-4">
            <Form.Label className={styles.sectionLabel}>
              Daily Upload Count
            </Form.Label>
            <Form.Control
              type="number"
              value={dailyUploadCount}
              onChange={handleNumberInput(setDailyUploadCount)}
              placeholder=""
              min="0"
              inputMode="numeric"
              pattern="[0-9]*"
              onKeyPress={blockNonNumeric}
              onPaste={sanitizePaste(setDailyUploadCount)}
            />
          </div>

          {/* Percentage Distribution Section */}
          <div className="mb-4">
            <Form.Label className={styles.sectionLabel}>
              Set percentage wise distribution for data type
            </Form.Label>

            {/* GST */}
            <div className={styles.percentageRow}>
              <Form.Label className={styles.percentageLabel}>GST</Form.Label>
              {gstRemaining !== null && (
                <span className={styles.remainingCount}>
                  (Remaining Leads: {gstRemaining})
                </span>
              )}
              <Form.Control
                type="number"
                value={gstPercentage}
                onChange={handleNumberInput(setGstPercentage)}
                placeholder=""
                min="0"
                max="100"
                inputMode="numeric"
                pattern="[0-9]*"
                onKeyPress={blockNonNumeric}
                onPaste={sanitizePaste(setGstPercentage)}
              />
              <span className={styles.percentageSymbol}>%</span>
              {gstValue && (
                <span className={styles.dividedCount}>{gstValue}</span>
              )}
            </div>

            {/* DIN */}
            <div className={styles.percentageRow}>
              <Form.Label className={styles.percentageLabel}>DIN</Form.Label>
              {dinRemaining !== null && (
                <span className={styles.remainingCount}>
                  (Remaining Leads: {dinRemaining})
                </span>
              )}
              <Form.Control
                type="number"
                value={dinPercentage}
                onChange={handleNumberInput(setDinPercentage)}
                placeholder=""
                min="0"
                max="100"
                inputMode="numeric"
                pattern="[0-9]*"
                onKeyPress={blockNonNumeric}
                onPaste={sanitizePaste(setDinPercentage)}
              />
              <span className={styles.percentageSymbol}>%</span>
              {dinValue && (
                <span className={styles.dividedCount}>{dinValue}</span>
              )}
            </div>

            {/* BCM */}
            <div className={styles.percentageRow}>
              <Form.Label className={styles.percentageLabel}>BCM</Form.Label>
              {bcmRemaining !== null && (
                <span className={styles.remainingCount}>
                  (Remaining Leads: {bcmRemaining})
                </span>
              )}
              <Form.Control
                type="number"
                value={bcmPercentage}
                onChange={handleNumberInput(setBcmPercentage)}
                placeholder=""
                min="0"
                max="100"
                inputMode="numeric"
                pattern="[0-9]*"
                onKeyPress={blockNonNumeric}
                onPaste={sanitizePaste(setBcmPercentage)}
              />
              <span className={styles.percentageSymbol}>%</span>
              {bcmValue && (
                <span className={styles.dividedCount}>{bcmValue}</span>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <Button
              variant="primary"
              type="submit"
              className={styles.submitButton}
              disabled={isFormIncomplete}
            >
              Submit
            </Button>
          </div>
        </Form>
      </div>
    </Layout>
  );
};

export default LeadsDataTransferSetting;
